import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import CardDropdown from 'components/common/CardDropdown';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import CustomAdvanceTableGiftAid from 'components/common/advance-table/CustomAdvanceTableGiftAid';
import Dropdown from 'react-bootstrap/Dropdown';
import LoadingContainer from 'components/common/LoadingContainer';
import { useAuthContext } from 'context/authContext';
import { format } from 'date-fns';
import { formatMoney, parseRouteParams } from 'helpers/utils';
import { Card, Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import { useState, useMemo } from 'react';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { toast } from 'react-toastify';
import { Can } from 'routes/permissions/Can';
import {
  permissionsKeyMap,
  userActions
} from 'routes/permissions/permissionKeys';
import {
  useGiftAidApplications,
  useGiftAidApplicationAggregates,
  useUpdateGiftAidApplicationStatus,
  useGiftAids
} from 'hooks/useGiftAid';

export const GIFT_AID_APPLICATION_AWAITING = 'awaiting';
export const GIFT_AID_APPLICATION_SUBMITTED = 'submitted';
export const GIFT_AID_APPLICATION_COMPLETED = 'completed';

export const giftAidApplicationStatus = {
  [GIFT_AID_APPLICATION_AWAITING]: {
    variant: 'warning',
    label: 'Awaiting'
  },
  [GIFT_AID_APPLICATION_SUBMITTED]: {
    variant: 'primary',
    label: 'Submitted'
  },
  [GIFT_AID_APPLICATION_COMPLETED]: {
    variant: 'success',
    label: 'Completed'
  }
};

const applicationToGiftAidStatusMap = {
  [GIFT_AID_APPLICATION_AWAITING]: 'inprogress',
  [GIFT_AID_APPLICATION_SUBMITTED]: 'inprogress',
  [GIFT_AID_APPLICATION_COMPLETED]: 'accepted'
};

const GiftAidApplications = () => {
  const navigate = useNavigate();
  const { organization } = useAuthContext();
  const [statusChangeState, setStatusChangeState] = useState({
    id: null,
    status: null
  });
  const [showChangeStatusConfirmation, setShowChangeStatusConfirmation] =
    useState(false);

  const {
    giftAidApplications,
    isLoading,
    error,
    refetch
  } = useGiftAidApplications();

  const { aggregates } = useGiftAidApplicationAggregates();

  const { giftAids: unclaimedGiftAids } = useGiftAids({
    status: 'unclaimed'
  });

  const { giftAids: acceptedGiftAids } = useGiftAids({ status: 'accepted' });

  const {
    mutate: updateGiftAidApplicationStatus,
    isPending: updateStatusLoading
  } = useUpdateGiftAidApplicationStatus({
    onSuccess: () => {
      hideChangeStatusConfirmation();
      toast.success('Gift Aid status updated successfully');
      refetch();
    },
    onError: error => {
      toast.error(error.message);
    }
  });

  const columns = [
    {
      accessor: 'ref',
      Header: 'Claim Ref',
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'date_generated',
      Header: 'Date Generated',
      cellProps: {
        className: 'py-2 fw-medium'
      }
    },
    {
      accessor: 'date_submitted',
      Header: 'Date Submitted',
      cellProps: {
        className: 'fw-medium py-2'
      }
    },
    {
      accessor: 'total_applicants',
      Header: 'Total Applicants',
      cellProps: {
        className: 'fw-medium py-2'
      }
    },
    {
      accessor: 'total_donations',
      Header: 'Total Donations',
      cellProps: {
        className: 'fw-medium py-2'
      },
      Cell: rowData => {
        return (
          <span>{formatMoney(rowData.row.original.total_donations, '£')}</span>
        );
      }
    },
    {
      accessor: 'gift_aid',
      Header: 'Gift Aid',
      cellProps: {
        className: 'fw-medium py-2'
      },
      Cell: rowData => {
        return <span>{formatMoney(rowData.row.original.gift_aid, '£')}</span>;
      }
    },
    {
      accessor: 'status',
      Header: 'Status',
      cellProps: {
        className: 'fw-medium py-2'
      },
      Cell: rowData => {
        const { status } = rowData.row.original;
        return (
          <SoftBadge pill bg={giftAidApplicationStatus[status].variant}>
            {giftAidApplicationStatus[status].label}
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'oid',
      Header: '',
      cellProps: {
        className: 'text-end fw-medium py-2'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row;
        const { status } = rowData.row.original;

        return (
          <CardDropdown>
            <Can
              I={userActions.update}
              a={permissionsKeyMap.giftAid.children.giftAid}
            >
              <div className="py-2">
                <Dropdown.Item
                // onClick={() => {
                //   setEditId(id);
                //   setIsEdit(true);
                // }}
                >
                  Edit
                </Dropdown.Item>

                {status !== GIFT_AID_APPLICATION_SUBMITTED && (
                  <Dropdown.Item
                    className={`text-${giftAidApplicationStatus[GIFT_AID_APPLICATION_SUBMITTED].variant}`}
                    onClick={() =>
                      handleChangeStatus(id, GIFT_AID_APPLICATION_SUBMITTED)
                    }
                  >
                    {
                      giftAidApplicationStatus[GIFT_AID_APPLICATION_SUBMITTED]
                        .label
                    }
                  </Dropdown.Item>
                )}
                {status !== GIFT_AID_APPLICATION_AWAITING && (
                  <Dropdown.Item
                    className={`text-${giftAidApplicationStatus[GIFT_AID_APPLICATION_AWAITING].variant}`}
                    onClick={() =>
                      handleChangeStatus(id, GIFT_AID_APPLICATION_AWAITING)
                    }
                  >
                    Awaiting
                  </Dropdown.Item>
                )}
                {status !== GIFT_AID_APPLICATION_COMPLETED && (
                  <Dropdown.Item
                    className={`text-${giftAidApplicationStatus[GIFT_AID_APPLICATION_COMPLETED].variant}`}
                    onClick={() =>
                      handleChangeStatus(id, GIFT_AID_APPLICATION_COMPLETED)
                    }
                  >
                    Completed
                  </Dropdown.Item>
                )}
              </div>
            </Can>
          </CardDropdown>
        );
      }
    }
  ];

  const handleChangeStatus = (id, status) => {
    setShowChangeStatusConfirmation(true);
    setStatusChangeState({
      id,
      status
    });
  };

  const hideChangeStatusConfirmation = () => {
    setShowChangeStatusConfirmation(false);
    setStatusChangeState({
      id: null,
      status: null
    });
  };

  const handleChangeStatusConfirm = () => {
    const selectedApplicationData = giftAidApplications.find(
      ({ id }) => id === statusChangeState.id
    );
    const giftAidIds = selectedApplicationData.gift_aids.map(({ id }) => id);
    const submittedDate =
      statusChangeState.status === GIFT_AID_APPLICATION_SUBMITTED
        ? new Date()
        : selectedApplicationData.submitted_date;
    updateGiftAidApplicationStatus({
      id: statusChangeState.id,
      status: statusChangeState.status,
      submittedDate: submittedDate,
      giftAidIds: giftAidIds,
      giftAidStatus: applicationToGiftAidStatusMap[statusChangeState.status]
    });
  };

  const listCounter = useMemo(() => {
    if (!aggregates) return [];

    const totalDonationsAmount = acceptedGiftAids.reduce(
      (sum, giftAid) => sum + (giftAid.amount || 0),
      0
    );

    return [
      {
        id: 1,
        title: 'Completed',
        value: aggregates.completedCount,
        color: '#00D27B'
      },
      {
        id: 2,
        title: 'Submitted',
        value: aggregates.submittedCount,
        color: '#2B7EED'
      },
      {
        id: 2,
        title: 'Awaiting',
        value: aggregates.awaitingCount,
        color: '#C46632'
      },
      {
        id: 3,
        title: 'Unclaimed',
        value: unclaimedGiftAids.length,
        color: '#1E1E1E'
      },
      {
        id: 4,
        title: 'Total Donations',
        value: formatMoney(totalDonationsAmount, '£'),
        color: '#5D6E82'
      }
    ];
  }, [aggregates, unclaimedGiftAids, acceptedGiftAids]);

  const tableData = useMemo(() => {
    if (!giftAidApplications) return [];
    return giftAidApplications.map(
      ({ id, status, created_at, submitted_date, gift_aids_aggregate }) => {
        const formattedGeneratedDate = format(
          new Date(created_at),
          'dd/MM/yyyy'
        );
        const formattedSubmittedDate = submitted_date
          ? format(new Date(submitted_date), 'dd/MM/yyyy')
          : null;
        return {
          id,
          ref: id,
          date_generated: formattedGeneratedDate,
          date_submitted: formattedSubmittedDate,
          total_applicants: gift_aids_aggregate.aggregate.count,
          total_donations: gift_aids_aggregate.nodes.reduce(
            (acc, { order }) => acc + (order?.donation_amount || 0),
            0
          ),
          gift_aid: gift_aids_aggregate.aggregate.sum.amount,
          status
        };
      }
    );
  }, [giftAidApplications]);

  if (isLoading) return <LoadingContainer />;
  if (error) return <p>Error! {error.message}</p>;
  return (
    <>
      <ConfirmationModal
        show={showChangeStatusConfirmation}
        onHide={hideChangeStatusConfirmation}
        title="Change Status"
        loading={updateStatusLoading}
        handleConfirm={handleChangeStatusConfirm}
      >
        <p>
          Are you sure you want to change the status of this application to{' '}
          <b>{giftAidApplicationStatus[statusChangeState.status]?.label}</b>?
        </p>
      </ConfirmationModal>

      <Row className="g-3 mb-3">
        <Col>
          <Card>
            <Card.Body className="mt-1">
              <Row className="g-3">
                {listCounter?.map((item, index) => (
                  <Col className="col-6" xl={2} key={item.id}>
                    <Flex
                      className={classNames({
                        'border-xl-end': index !== listCounter.length - 1
                      })}
                    >
                      <FontAwesomeIcon
                        icon={'circle'}
                        className={'fs--2 mt-1'}
                        color={item.color}
                      />
                      <div className="ms-2">
                        <p className="mb-2 fw-medium">{item.title}</p>
                        <p className="mb-0 fs-2 fw-medium">{item.value}</p>
                      </div>
                    </Flex>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTableGiftAid
            data={tableData}
            columns={columns}
            perPage={50}
            showTablePagination={false}
            title="Claimed Gift Aid Applications"
            showSearchInput
            searchInputPlaceHolder="Search"
            addButtonIcon="plus"
            addButtonLabel="Generate A New Claim"
            hideBulkActions={true}
            selection={false}
            showFilterButton={false}
            showExportButton={false}
            // isEditable={isEdit}
            // editId={editId}
            // handleSave={handleSave}
            // handleRowChange={handleRowChange}
            onClickAddNew={() => navigate(paths.giftAidApplicationCreate)}
            rowClickEvent={(e, rowData) => {
              if (
                e.target.classList.contains('btn-reveal', 'dropdown-toggle') ||
                e.target.parentElement.classList.contains(
                  'btn-reveal',
                  'dropdown-toggle'
                ) ||
                e.target.classList.contains('dropdown-menu') ||
                e.target.classList.contains('dropdown-item') ||
                e.target.tagName === 'INPUT' ||
                e.target.tagName === 'BUTTON'
              ) {
                e.stopPropagation();
                return;
              }
              navigate(
                parseRouteParams(paths.giftAidApplicationDetails, {
                  id: rowData.id
                })
              );
            }}
            subject={permissionsKeyMap.giftAid.children.giftAid}
          />
        </Col>
      </Row>
    </>
  );
};

export default GiftAidApplications;
